/**
 * Read-only grid of uploaded prescription photos with open-full-size + download.
 * Shared by the owner-facing surfaces (booking record modal, pet vet records).
 */
import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { AuthImage, open_file_in_new_tab, download_file } from '@/components/auth-image';
import { form_error_message } from '@/lib/forms';

export type PrescriptionDocView = { id: string; thumb_path: string; full_path: string };

export function PrescriptionDocGrid({
  docs,
  size = 'md',
  testid_prefix = 'rx-doc',
}: {
  docs: PrescriptionDocView[];
  size?: 'sm' | 'md';
  testid_prefix?: string;
}) {
  const [busy_id, set_busy_id] = useState<string | null>(null);
  const [error, set_error] = useState<string | null>(null);
  const box = size === 'sm' ? 'h-16 w-16' : 'h-20 w-20';

  async function handle_download(doc: PrescriptionDocView, index: number) {
    set_error(null);
    set_busy_id(doc.id);
    try {
      await download_file(doc.full_path, `prescription-${index + 1}`);
    } catch (err) {
      set_error(form_error_message(err, 'Could not download this prescription.'));
    } finally {
      set_busy_id(null);
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap gap-2">
        {docs.map((d, i) => (
          <div key={d.id} className="relative" data-testid={`${testid_prefix}-${d.id}`}>
            <button
              type="button"
              onClick={() => open_file_in_new_tab(d.full_path)}
              className={`block ${box} overflow-hidden rounded-input border-2 border-brand-outline-variant/20`}
              title="Open full size"
            >
              <AuthImage path={d.thumb_path} alt="Prescription" className="h-full w-full object-cover" />
            </button>
            <button
              type="button"
              onClick={() => handle_download(d, i)}
              disabled={busy_id === d.id}
              aria-label="Download prescription"
              title="Download"
              data-testid={`${testid_prefix}-download-${d.id}`}
              className="absolute -bottom-1.5 -right-1.5 rounded-full bg-primary p-1 text-primary-foreground shadow disabled:opacity-60"
            >
              {busy_id === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            </button>
          </div>
        ))}
      </div>
      {error && (
        <p className="text-xs text-destructive" data-testid={`${testid_prefix}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
